import { Box } from "@mui/material";
import { useEffect, useState } from "react";

interface MobileFooterLayoutProps {
  children: React.ReactNode;
  hideWhenKeyboardOpen?: boolean;
  mobileSpacerHeight?: string;
}

export default function MobileFooterLayout({
  children,
  hideWhenKeyboardOpen = false,
  mobileSpacerHeight,
}: MobileFooterLayoutProps) {
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    if (!hideWhenKeyboardOpen) return;

    const viewport = window.visualViewport;
    let expandedHeight = viewport?.height ?? window.innerHeight;
    let wasOpen = false;
    const updateKeyboardVisibility = () => {
      const active = document.activeElement;
      const inputFocused = active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable);
      const visibleHeight = viewport?.height ?? window.innerHeight;
      if (!inputFocused && !wasOpen) {
        expandedHeight = visibleHeight;
      }
      wasOpen = expandedHeight - visibleHeight > 120 && (inputFocused || wasOpen);
      setKeyboardOpen(wasOpen);
    };

    viewport?.addEventListener("resize", updateKeyboardVisibility);
    window.addEventListener("resize", updateKeyboardVisibility);
    document.addEventListener("focusin", updateKeyboardVisibility);
    document.addEventListener("focusout", updateKeyboardVisibility);
    return () => {
      viewport?.removeEventListener("resize", updateKeyboardVisibility);
      window.removeEventListener("resize", updateKeyboardVisibility);
      document.removeEventListener("focusin", updateKeyboardVisibility);
      document.removeEventListener("focusout", updateKeyboardVisibility);
    };
  }, [hideWhenKeyboardOpen]);

  const showAtPageEnd = hideWhenKeyboardOpen && keyboardOpen;

  return (
    <>
      {mobileSpacerHeight && !showAtPageEnd && (
        <Box sx={{ height: mobileSpacerHeight, display: { xs: "block", md: "none" } }} />
      )}
      <Box
        sx={{
          position: { xs: showAtPageEnd ? "relative" : "fixed", md: "relative" },
          width: "100%",
          left: 0,
          bottom: 0,
          bgcolor: "background.default",
          borderTop: 1,
          borderColor: "divider",
          px: 2,
          pt: 1.75,
          pb: 2.25,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        {children}
      </Box>
    </>
  )
}
